import { useEffect } from 'react';
import { Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import type { PermissionKey } from '@evimo/types/enums/permission-key';
import type { RoleWithPermissionsDto } from '~/app/services/authorization/role.service';
import { RoleTypeGroupedSelect } from '../RoleTypeGroupedSelect/RoleTypeGroupedSelect';
import { ConfirmDialog } from '~/app/components/common/ConfirmDialog/ConfirmDialog';

type CreateRoleFormValues = {
  name: string;
  cloneFromRoleId?: string;
};

type CreateRoleModalProps = {
  open: boolean;
  roles: RoleWithPermissionsDto[];
  loading?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: {
    name: string;
    cloneFromRoleId?: string;
    permissionKeys?: PermissionKey[];
  }) => Promise<void>;
};

export function CreateRoleModal({
  open,
  roles,
  loading,
  onOpenChange,
  onSubmit,
}: CreateRoleModalProps) {
  const { t } = useTranslation('authorization');
  const [form] = Form.useForm<CreateRoleFormValues>();

  useEffect(() => {
    if (!open) {
      form.resetFields();
    }
  }, [form, open]);

  const handleConfirm = async () => {
    try {
      const values = await form.validateFields();
      const sourceRole = values.cloneFromRoleId
        ? roles.find((role) => role.id === values.cloneFromRoleId)
        : undefined;

      await onSubmit({
        name: values.name.trim(),
        cloneFromRoleId: values.cloneFromRoleId,
        permissionKeys: sourceRole?.permissionKeys,
      });
    } catch {
      // validation errors are shown by the form
    }
  };

  return (
    <ConfirmDialog
      open={open}
      variant="primary"
      title={t('createModal.title')}
      description={t('createModal.subtitle')}
      confirmLabel={t('createModal.submit')}
      cancelLabel={t('createModal.cancel')}
      loading={loading}
      onCancel={() => onOpenChange(false)}
      onConfirm={handleConfirm}
    >
      <Form form={form} layout="vertical" className="app-confirm-dialog-form">
        <Form.Item
          name="name"
          label={t('createModal.nameLabel')}
          rules={[
            { required: true, message: t('createModal.nameRequired') },
            { max: 100, message: t('createModal.nameMaxLength') },
          ]}
        >
          <Input placeholder={t('createModal.namePlaceholder')} />
        </Form.Item>

        <Form.Item name="cloneFromRoleId" label={t('createModal.cloneLabel')}>
          <RoleTypeGroupedSelect
            allowClear
            roles={roles}
            placeholder={t('createModal.clonePlaceholder')}
          />
        </Form.Item>
      </Form>
    </ConfirmDialog>
  );
}
